import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetTaxReportHtmlQuery } from "../../../redux/apiStore";

export function TaxReportPage() {
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hasPrintedRef = useRef<boolean>(false);

  const familyId = useMemo(() => {
    const value = searchParams.get("familyId");
    return value ? Number(value) : undefined;
  }, [searchParams]);

  const year = useMemo(() => {
    const value = searchParams.get("year");
    return value ? Number(value) : undefined;
  }, [searchParams]);

  const lf = useMemo(() => {
    return searchParams.get("lf") ?? undefined;
  }, [searchParams]);

  const yearTotal = useMemo(() => {
    const value = searchParams.get("yearTotal");
    return value ? Number(value) : undefined;
  }, [searchParams]);

  const isValidRequest = !!familyId && !!year;

  const reportHtmlQuery = useGetTaxReportHtmlQuery(
    {
      familyId,
      year,
      lf,
      yearTotal,
    },
    { skip: !isValidRequest },
  );

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
      return;
    }

    window.print();
  };

  useEffect(() => {
    hasPrintedRef.current = false;
  }, [familyId, year, lf, yearTotal]);

  if (!isValidRequest) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Missing query parameters. A familyId and year are required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", minHeight: 500, overflow: "hidden", backgroundColor: "#ffffff" }}>
      <Stack sx={{ height: "100%", overflow: "hidden", backgroundColor: "#ffffff" }}>
        {reportHtmlQuery.isFetching && (
          <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading tax report...
            </Typography>
          </Stack>
        )}

        {!reportHtmlQuery.isFetching && reportHtmlQuery.isError && (
          <Stack sx={{ p: 2 }}>
            <Alert severity="error">Unable to load tax report HTML.</Alert>
          </Stack>
        )}

        {!reportHtmlQuery.isFetching && !!reportHtmlQuery.data && (
          <iframe
            ref={iframeRef}
            title="Tax Report"
            srcDoc={reportHtmlQuery.data}
            onLoad={() => {
              if (hasPrintedRef.current) {
                return;
              }

              hasPrintedRef.current = true;
              handlePrint();
            }}
            style={{ width: "100%", height: "100%", border: 0, backgroundColor: "#ffffff" }}
          />
        )}
      </Stack>
    </Box>
  );
}
